// Tiny presentational primitives shared across multiple Dashboard widgets.
// Kept dependency-free so they can be re-used by future widgets without
// pulling in everything else from DashboardPage.

import { progressBarColor } from '@/utils/styleHelpers';
import { BRAND } from './_constants';

export const HealthRow = ({ label, value, color }) => (
  <div className="flex items-center justify-between py-2.5 border-b border-muted/50 last:border-b-0">
    <span className="text-sm font-medium text-muted-foreground">{label}</span>
    <span className={`text-sm font-semibold ${color || ''}`}>{value}</span>
  </div>
);

export const ScoreRing = ({ score, size = 100, label }) => {
  const radius = (size - 12) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference - (score / 100) * circumference;
  const color = progressBarColor(score, BRAND, 80, 60, 0);
  return (
    <div className="flex flex-col items-center gap-1">
      <svg width={size} height={size} className="transform -rotate-90">
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e5e7eb" strokeWidth="8" />
        <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke={color}
          strokeWidth="8" strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" className="transition-all duration-700" />
      </svg>
      <div className="absolute flex flex-col items-center justify-center" style={{ width: size, height: size }}>
        <span className="text-2xl font-bold">{score}%</span>
      </div>
      {label && <span className="text-xs text-muted-foreground mt-1">{label}</span>}
    </div>
  );
};

export const ModuleProgressBar = ({ label, value, max, icon: Icon, color }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div className="flex items-center gap-3 py-1.5">
      <Icon className="h-4 w-4 flex-shrink-0" style={{ color }} />
      <span className="text-sm w-24 truncate">{label}</span>
      <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-500" style={{ width: `${pct}%`, backgroundColor: color }} />
      </div>
      <span className="text-xs font-mono w-10 text-right text-muted-foreground">{pct}%</span>
    </div>
  );
};

export const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-border rounded-lg shadow-lg px-3 py-2 text-xs">
      <p className="font-semibold mb-1">{label}</p>
      {payload.map((p) => (
        <p key={`${p.name}-${p.color}`} style={{ color: p.color }}>
          {p.name}: {p.value}{typeof p.value === 'number' && p.value <= 100 ? '%' : ''}
        </p>
      ))}
    </div>
  );
};

// Returns the ISO week id (YYYY-Www) of the previous *completed* week.
export const previousIsoWeek = (date = new Date()) => {
  const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
  const day = d.getUTCDay() || 7;
  d.setUTCDate(d.getUTCDate() - day - 6); // back to Monday of previous week
  d.setUTCDate(d.getUTCDate() + 3);       // Thursday of that week (ISO calc anchor)
  const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
  const weekNo = Math.ceil(((d - yearStart) / 86400000 + 1) / 7);
  return `${d.getUTCFullYear()}-W${String(weekNo).padStart(2, '0')}`;
};

export const fmtMins = (m) => {
  if (!m || m <= 0) return '0:00';
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
};
